import React, { useMemo, useState } from "react";
import { observer } from "mobx-react-lite";
import cx from "classnames";
import { Hooks } from "@xt/xtbase";
import store from "store";
import styles from "./index.module.scss";
import { Upload, message } from "antd";
import type { RcFile, UploadChangeParam, UploadFile } from "antd/lib/upload";
import XtLoading from "@/components/Xt/Loading";
interface UploadProps extends BaseCompProps {
  attr?: any;
  action?: string;
  type?: string;
  name?: string;
  params?: string;
  limit?: number | string;
  accept?: string;
  /** 当前值（如表单中的 videoUrl），有值时显示已上传状态并隐藏父级占位背景 */
  value?: string;
  /** PC Spikex form: hide default add icon; parent renders lucide placeholder */
  spikexPc?: boolean;
  onChangeImg?(params: string, resData: any): void;
  onLoadingChange?(loading: boolean): void;
}
const { useTranslation } = Hooks;

function pickUploadUrl(response: any): string {
  if (!response) return "";
  const payload = response.data !== undefined ? response.data : response.result !== undefined ? response.result : response;
  if (!payload) return "";
  if (typeof payload === "string") return payload;
  if (Array.isArray(payload)) {
    const first = payload[0];
    if (!first) return "";
    if (typeof first === "string") return first;
    return first.absolutePath || first.url || first.fileUrl || first.path || "";
  }
  if (typeof payload === "object") {
    return payload.absolutePath || payload.url || payload.fileUrl || payload.path || (typeof payload[0] === "string" ? payload[0] : "") || "";
  }
  return "";
}

const AuthUpload: React.FC<UploadProps> = ({
  attr,
  className,
  action = "/exchange-user/api/web/1_0_0/uploadFile",
  type = "img",
  name = "file",
  params = "",
  limit = 10240,
  accept = ".jpg,.png,.jpeg,.svg,.gif,.webp",
  value: valueProp,
  spikexPc = false,
  onChangeImg,
  onLoadingChange,
}) => {
  const t = useTranslation();
  const { token } = store.user;
  const [loading, setLoading] = useState(false);
  const [imageUrl, setImageUrl] = useState<string | ArrayBuffer | null>("");
  const [file, setFile] = useState({} as RcFile);

  const setBusy = (busy: boolean) => {
    setLoading(busy);
    onLoadingChange?.(busy);
  };

  const imgType = useMemo(() => {
    return attr?.imgType;
  }, [attr]);
  /** useMemo */
  const getHeaders = useMemo(() => {
    return {
      token,
      device: "web",
      Authorization: `Bearer ` + token,
    };
  }, [token]);
  const getBase64 = (img: File, callback: (imageUrl: string | ArrayBuffer | null) => void) => {
    const reader = new FileReader();
    reader.addEventListener("load", () => callback(reader.result));
    reader.readAsDataURL(img);
  };
  const handleChange = (info: UploadChangeParam<UploadFile<any>>) => {
    if (info.file.status === "uploading") {
      setBusy(true);
      return;
    }
    if (info.file.status === "error") {
      setBusy(false);
      message.error(t("account.kycUploadFail"));
      return;
    }
    if (info.file.status === "done") {
      const url = pickUploadUrl(info.file.response);
      if (!url) {
        setBusy(false);
        message.error(t("account.kycUploadFail"));
        return;
      }
      onChangeImg && onChangeImg(params, url);
      if (type === "img" && info.file.originFileObj) {
        getBase64(info.file.originFileObj, (preview) => {
          setImageUrl(preview);
          setBusy(false);
        });
        return;
      }
      setImageUrl(url);
      setBusy(false);
    }
  };

  const beforeUpload = (file: RcFile) => {
    const isMb = limit && limit == 5120 ? 5120 : 51200;
    const isLt2M = file.size / 1024 < isMb;
    setFile(file);
    if (!isLt2M) {
      message.error(t("account.notAllowed", [isMb]));
      return false;
    }
    return true;
  };
  const hasPreview = Boolean(imageUrl || valueProp);
  return (
    <div className={cx(styles.auth_upload, className)}>
      <Upload
        className={styles["uploadAuth"]}
        showUploadList={false}
        action={action}
        name={name}
        onChange={handleChange}
        accept={accept}
        beforeUpload={beforeUpload}
        headers={getHeaders}
      >
        {type == "img" ? (
          <div className={styles["img_S"]}>
            {hasPreview ? (
              <div className={styles["imgurl_class"]}>
                <img
                  className={styles["realimg"]}
                  src={(imageUrl || valueProp) as string}
                  alt="avatar"
                />
              </div>
            ) : spikexPc ? (
              <div className={styles.spikexUploadHit} aria-hidden />
            ) : (
              <div className={styles["upload_icon"]}>
                <img
                  className={cx(styles["ll-add-icon"], imgType ? styles["kyc"] : "")}
                  src={imgType ? require("@/assets/images/account/updatafile.png") : require("@/assets/images/account/a-file-add.png").default}
                  alt="images"
                />
              </div>
            )}
          </div>
        ) : (
          <div>
            {hasPreview ? (
              <div className={cx(styles["imgurl_class"], styles["imgurl_class_video"])}>
                <div style={{ textAlign: "center" }}>
                  <img src={require("@/assets/images/account/videos.png").default} alt="avatar" />
                  <p style={{ marginTop: "10px" }}>{file?.name || (valueProp ? valueProp.split("/").pop() || valueProp : "")}</p>
                </div>
              </div>
            ) : spikexPc ? (
              <div className={styles.spikexUploadHit} aria-hidden />
            ) : (
              <div className={styles["upload_icon"]}>
                <img className={styles["ll-add-icon"]} src={require("@/assets/images/account/a-file-add.png").default} alt="video" />
              </div>
            )}
          </div>
        )}
      </Upload>
      {loading ? (
        <div className={styles.uploadLoading}>
          <XtLoading />
        </div>
      ) : null}
    </div>
  );
};
export default observer(AuthUpload);
